// Product option modal — price math. Pure: given the current selection it
// returns every figure the modal shows and the unit price sent to the cart.
//
// Rules:
//  - Variant-SKU items: the selected SKU's selling price IS the unit price
//    (variant deltas are not added). No SKU picked yet => price is pending.
//  - Other items: unit price = item price (converted to the chosen alternative
//    unit) + sum of variant deltas (scaled by the unit's conversion rate).
//  - Modifier / custom-modifier / combo / upsell / service-item deltas are
//    added once per line, not per unit.
import { firstNumber } from './normalize';
import { resolveAlternativeUnitPrice } from './units';

const sum = (list, pick) => list.reduce((total, item) => total + (pick(item) || 0), 0);

/**
 * @param {object} input
 * @param {object} input.product
 * @param {boolean} input.isVariantSku
 * @param {object|null} input.selectedSku
 * @param {object[]} input.selectedVariants      [{ priceDelta }]
 * @param {object[]} input.selectedModifiers     [{ priceDelta, quantity }]
 * @param {object[]} input.selectedServiceItems  payload service items [{ price_delta }]
 * @param {object|undefined} input.alternativeUnit  chosen alternative-unit conversion
 * @param {number} input.quantity
 * @param {number} [input.modifierCustomDelta]
 * @param {number} [input.comboDelta]
 * @param {number} [input.upsellDelta]
 */
export function computeOptionPricing({
  product,
  isVariantSku,
  selectedSku,
  selectedVariants,
  selectedModifiers,
  selectedServiceItems,
  alternativeUnit,
  quantity,
  modifierCustomDelta = 0,
  comboDelta = 0,
  upsellDelta = 0,
}) {
  const isPricePending = isVariantSku && !selectedSku;
  const baseUnitPrice = isVariantSku
    ? selectedSku
      ? (firstNumber(selectedSku.sellingPrice, product.price, 0) ?? 0)
      : 0
    : (firstNumber(product.price, 0) ?? 0);
  const variantDelta = isVariantSku ? 0 : sum(selectedVariants, (option) => option.priceDelta);
  const serviceItemDelta = sum(selectedServiceItems, (item) => item.price_delta);
  const modifierDelta = sum(
    selectedModifiers,
    (option) => (option.priceDelta || 0) * option.quantity,
  );
  const conversionRate =
    firstNumber(alternativeUnit?.conversion_rate, alternativeUnit?.conversionRate, 1) ?? 1;
  const alternativeUnitPrice = resolveAlternativeUnitPrice(alternativeUnit, baseUnitPrice);
  const unitPrice = alternativeUnitPrice + variantDelta * conversionRate;
  const lineExtras =
    modifierDelta + modifierCustomDelta + comboDelta + upsellDelta + serviceItemDelta;

  return {
    isPricePending,
    baseUnitPrice,
    variantDelta,
    modifierDelta,
    serviceItemDelta,
    conversionRate,
    /** unit price sent to the cart */
    unitPrice,
    /** one-off extras on top of unitPrice × quantity (the cart's "modExtra") */
    lineExtras,
    lineTotal: unitPrice * quantity + lineExtras,
    displayPrice: isVariantSku && selectedSku ? baseUnitPrice : product.price,
    displayMrp: isVariantSku && selectedSku ? selectedSku.mrp : product.mrp,
  };
}
